import { useEffect, useState } from 'react'

import AvatarCircle from './AvatarCircle.jsx'
import HoopsPanel from './hoops/HoopsPanel.jsx'
import MoviePanel from './MoviePanel.jsx'
import MusicPanel from './MusicPanel.jsx'
import ReleaseBar from './ReleaseBar.jsx'
import { StubPanel, TripsPanel } from './SidePanels.jsx'
import WatchPanel from './WatchPanel.jsx'
import { branchesFor, featureRow } from './branches.js'
import { READY as MOVIE_READY, slotLabel, useMovie } from './movie.js'
import { useNow } from './useNow.js'
import { commands, sectionList, releases } from '../content.js'
import { UNKNOWN_NAME, directoryNote, nameFor, useDirectory } from '../directory.js'
import { listTrips } from '../trips/api.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The portal home — the signed-in face of `/`. P6-83, spec §4.1.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHY IT IS `/` AND NOT A NEW ROUTE
// ═══════════════════════════════════════════════════════════════════════════
//
// MP1: *"Members are the primary audience. Signed-in members land on the portal
// home; the logged-out face is a clean, small public site. Nav INVERSION, not
// addition."* One URL, two faces — which is also what the shell already
// assumes: `Layout.jsx`'s tab set is Home · Docs · Commands · Trips for a
// signed-in member, and Home is `/`.
//
// A `/portal` section would have needed a registry entry, a nav item and a tab,
// and would have made "home" mean two different places depending on who you are
// — the crowding MP4/MP5 exist to avoid, arriving on day one.
//
// ⚠️ SO THE DOCUMENT AT `/` STAYS TIER 1 AND PUBLIC, AND THAT IS CORRECT RATHER
// THAN A COMPROMISE. ADR-0003 D5: *"a static bundle is public by construction,
// on any host. That is exactly why D5 requires personal data to arrive through a
// Function at runtime and never to be built into the site: there is nothing
// crew-shaped in the bundle to protect."* Everything personal on this page
// arrives from `/api/movie`, which establishes its own caller and returns the
// caller's own 3b object and nothing else. A signed-out browser loading this
// bundle gets the public landing and no request it makes is answered with
// anybody's history.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NOTHING ON THIS PAGE WRITES ANYWHERE (ADR-0005 D2)
// ═══════════════════════════════════════════════════════════════════════════
//
// *"No path from the site to any bot-owned store exists, opens, or is
// implied."* There is no form, no submit, no button that changes anything —
// every control here either opens a disclosure or is a link to a page that
// already exists. `src/test/portal/portal.test.jsx` asserts that against the
// rendered document rather than against this comment.
//
// ═══════════════════════════════════════════════════════════════════════════
// ✅ THE FEED IS NOT ON THIS PAGE ANY MORE — P6-242b / P6-285
// ═══════════════════════════════════════════════════════════════════════════
//
// This block used to be the history of the feed's second column: P6-182 refused
// it on a measurement, wrote a falsifiable retirement condition next to the
// refusal (*"THE FEED IS RIGHT WHEN A SECOND DATED SOURCE PUBLISHES"*), the
// condition fired when music published dated events, and Jordan ruled *"let's do
// the feed"*. That history lives in `activity.js`, where the feed's data is.
//
// What changed here: Jordan, 2026-09-24 — *"keep the cross cog history in one
// location, even if it needs to be small and expanded in some way to be
// scrolled … move it into the header."* The feed had two homes depending on the
// pill (below the panel for music and watch, beside it for the rest) and took a
// column from every panel to hold it. It is now the second row of the sticky
// header on every signed-in route (`Layout.jsx`, `ActivityFeed.jsx`), and its
// reads left with it: this page no longer calls `useMusicOnce` or a second
// `useWatch` for a feed it does not draw.
//
// 🔴 SO EVERY PANEL RUNS THE WHOLE ROW. P6-210 gave music the full 1080px and V2-8b
// gave watch the same; with nothing beside the open panel the other four get it
// too, and `data-open` — the attribute that named the two exceptions — is gone
// with the exceptions. `src/test/viewport.test.js` measures the panel's width at
// every pill rather than reading this comment.

/** The trips module's three states. Its failure is never the page's. */
const TRIPS_LOADING = { status: 'loading', list: [] }

function useTrips() {
  const [trips, setTrips] = useState(TRIPS_LOADING)
  useEffect(() => {
    let cancelled = false
    listTrips().then(
      (list) => {
        if (!cancelled) setTrips({ status: 'ready', list: Array.isArray(list) ? list : [] })
      },
      // 🔴 A REJECTION HANDLER, NOT A `.catch` ON A CHAIN THAT ALSO CARRIES THE
      // SUCCESS PATH. `listTrips` throws BoardError; a catch after `.then` would
      // also swallow an exception thrown by setTrips and report it as the trip
      // service being down.
      () => {
        if (!cancelled) setTrips({ status: 'error', list: [] })
      },
    )
    return () => {
      cancelled = true
    }
  }, [])
  return trips
}

/**
 * The version mark. MP6, and it is DERIVED rather than typed.
 *
 * *"The portal wears the platform version mark ('D2D 2.0') — the only place
 * besides release notes a version appears."* The handoff adds the half that
 * matters: it comes from the newest PUBLISHED release (releases spec RL6), not
 * from a literal string here.
 *
 * ⚠️ SO IT IS ABSENT UNTIL SOMETHING IS PUBLISHED, and that is the rule this
 * whole page is built on rather than an oversight. `content/releases.json`
 * carries `version: null` while every release is still a draft — a pill reading
 * "D2D 2.0" today would be announcing an unpublished release, which is the leak
 * src/routes/Releases.jsx already refuses on its own empty state.
 */
function VersionMark() {
  const version = typeof releases.version === 'string' ? releases.version.trim() : ''
  if (version === '') return null
  return (
    <span className="d2d-portal__version d2d-micro" title="Platform version">
      <span className="d2d-portal__versiondot" aria-hidden="true" />
      D2D {version}
    </span>
  )
}

/**
 * V2-8a: the identity circle moved to `AvatarCircle.jsx` — one shared
 * component, imported here rather than defined locally, so the title
 * page's own member circles (`WatchTitle.jsx`'s group progress lines) draw
 * a Discord avatar the SAME way this page always has, per
 * RECONCILIATION.md item 4. Every reasoning note that used to live in this
 * file's own docstring (the letter is a first-class state, hotlinking's
 * written retirement condition, the 404 fallback, keying `broken` on the
 * URL) is preserved verbatim in `AvatarCircle.jsx` — nothing about this
 * page's own rendering changed, which is why `className`/`imgClassName`/
 * `testId` below are the ORIGINAL `.d2d-portal__avatar` /
 * `.d2d-portal__avatarimg` / `portal-avatar-image` this page has always
 * used, not the shared component's own generic 24/30/36/40px sizing.
 */

/** The one-line state under somebody's name. Only what the blob says. */
function subline(movie) {
  if (movie.state !== MOVIE_READY || movie.mineFailure !== null) return null
  if (movie.mine === null) return 'New here'
  if (movie.mine.in_rotation !== true) return 'Not in the rotation yet'
  const position = movie.mine.rotation_position
  // 🔴 NO DATE. The design's frame reads "you pick next on 24 Sep"; the guild's
  // movie-night day is not published and the publisher refuses to derive a date
  // from a week key on purpose. Position is published; a date is not.
  // `slotLabel`, not a literal — P6-177. This string and MoviePanel's rotation
  // heading are the same fact about the same member on the same screen, and
  // they were spelled two ways until the noun got one home.
  return Number.isInteger(position) ? `In the rotation · ${slotLabel(position)}` : 'In the rotation'
}

export default function PortalHome({ userId }) {
  useDocumentTitle('Your D2D')
  const directory = useDirectory()
  const movie = useMovie()
  const trips = useTrips()
  const now = useNow()

  const branches = branchesFor(commands.features)
  const [openId, setOpenId] = useState(branches[0]?.id ?? null)
  const open = branches.find((branch) => branch.id === openId) ?? branches[0] ?? null
  // 🔴 P6-316. THE DOCS PAGE THE OPEN PANEL LINKS TO, DERIVED ONCE. This expression
  // used to be typed out twice — once for `MusicPanel`, once for `StubPanel` — and
  // the other panels were never handed it: watch carried a correct `docSection`
  // that drew nothing, and movie night's link was a literal path inside
  // `MoviePanel.jsx`. `branches.js` says which page a branch means and the registry
  // owns where it lives, so a page that moves moves every link with it and no panel
  // types a path.
  //
  // ⚠️ `TripsPanel` IS NOT HANDED IT, ON PURPOSE. Trips' `docSection` is the board's
  // own route (`/trips`), not a docs page, and that panel already links to the
  // board. `NO_DOCS_LINK` in `src/test/portal/doc-links.test.jsx` carries the
  // reason, and the one change that retires it.
  const docPath = sectionList.find((section) => section.id === open?.docSection)?.path ?? null

  const you = nameFor(directory, typeof userId === 'string' ? userId : null)
  const initial = you.known ? you.label.trim().charAt(0).toUpperCase() : '·'
  const state = subline(movie)
  const note = directoryNote(directory)

  return (
    <article className="d2d-page d2d-portal" data-testid="portal-home">
      <header className="d2d-portal__identity">
        {/* ✅ THE SOURCE ARRIVED, SO THE PARAGRAPH THAT USED TO BE HERE IS GONE
            RATHER THAN AMENDED. It read: *"AN INITIAL, NOT AN AVATAR, AND IT IS
            A SOURCING FACT — this surface wants them and nothing publishes them
            yet, so the designed fallback is the whole implementation until one
            does."* P6-201 published them; the fallback stayed. */}
        <AvatarCircle
          key={you.avatarUrl ?? 'letter'}
          person={you}
          initial={initial}
          className="d2d-portal__avatar"
          imgClassName="d2d-portal__avatarimg"
          testId="portal-avatar-image"
        />
        <span className="d2d-portal__who">
          <h1 className="d2d-portal__name">{you.known ? you.label : UNKNOWN_NAME}</h1>
          {state ? <p className="d2d-portal__state">{state}</p> : null}
        </span>
        <VersionMark />
      </header>

      <ReleaseBar />

      {/* ⚠️ A LIST OF LINK-SHAPED BUTTONS, NOT A TABLIST. They swap a panel in
          the same document rather than navigating, and a real `role="tablist"`
          would owe arrow-key roving focus, `aria-controls` and `tabindex=-1` on
          every unselected pill — a keyboard contract that is easy to half-build
          and worse than none. `aria-current` says which one is showing, which is
          what the row actually means. */}
      <nav className="d2d-portal__branches" aria-label="Your stuff">
        <ul>
          {branches.map((branch) => {
            const selected = open?.id === branch.id
            // 🔴 NEVER A ZERO ON A PILL, EVEN A MEASURED ONE — and this line was
            // written the other way first, and this repo's own rule caught it in
            // its own test. The handoff: *"until a source exists for a pill's
            // count, render the pill with no count rather than a zero."* A `0`
            // badge reads as a notification count of nothing rather than as "you
            // have no trips", and the panel behind it already says the true
            // sentence. The absence of a badge is not a claim; a `0` is.
            const live = branch.id === 'trips' && trips.status === 'ready' ? trips.list.length : 0
            const count = live > 0 ? live : null
            return (
              <li key={branch.id}>
                <button
                  type="button"
                  className="d2d-portal__branch"
                  aria-current={selected ? 'true' : 'false'}
                  data-selected={selected ? 'true' : 'false'}
                  onClick={() => setOpenId(branch.id)}
                >
                  <span className="d2d-portal__branchdot" aria-hidden="true" />
                  <span>{branch.label}</span>
                  {/* 🔴 NO SOURCE, NO COUNT — never a zero. P6-156's rule, and
                      the publisher's own: "a count is a claim; an absence is
                      not." Trips is the only branch with a live number today. */}
                  {count === null ? null : (
                    <span className="d2d-portal__branchcount d2d-micro">{count}</span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* 🔴 P6-210, V2-8b AND P6-242b: THE OPEN PANEL RUNS THE WHOLE ROW, FOR
          EVERY PILL. P6-210 gave music the full 1080px (its canvas
          composition — a 420px cover with the title BESIDE it — needs ~870px
          and never fitted in a 532px track), and V2-8b gave watch the same on
          Jordan's own ruling (2026-09-24): "our cross-cog history here should
          be moved to the bottom if necessary like in the music cog if it is
          making our window smaller than what the design had."

          The other four pills kept a two-column row because the feed sat in
          the second column. The feed is in the header now (P6-285), so nothing
          is beside any panel and every panel gets the width — "the panels get
          their width back", the header handoff's own sentence. `data-open`,
          which named the two exceptions, went with the exceptions.

          ⚠️ THE WRAPPER STAYS, AND IT IS NOT DEAD. `.d2d-portal__columns` is a
          one-track grid that clips nothing and stretches nothing; the panel is
          still its child, so the 375px gate's `.d2d-portal__columns` selector
          and every panel's `min-width: 0` keep the same parent they always had. */}
      <div className="d2d-portal__columns">
        <div className="d2d-portal__open">
          {open === null ? (
            <p className="d2d-portal__flat">Nothing is set up on this server yet.</p>
          ) : open.kind === 'movie' ? (
            <MoviePanel movie={movie} directory={directory} now={now} docPath={docPath} />
          ) : open.kind === 'trips' ? (
            <TripsPanel trips={trips} />
          ) : open.kind === 'music' ? (
            // 🔴 THE PANEL FETCHES AND POLLS ITS OWN PROJECTION, WHICH IS WHY
            // `useMusic()` IS NOT CALLED UP THERE BESIDE `useMovie()`. MI7 wants
            // a two-second poll *while the panel is visible*, and this selector
            // renders one panel at a time — so mounting IS "open" and unmounting
            // IS "closed", with no flag to keep in step. A polling hook at page
            // level would poll for a panel nobody has opened, on every load.
            //
            // ⚠️ THE HEADER FEED READS `/api/music` ONCE TOO (`useMusicOnce`, in
            // `Layout.jsx`'s signed-in strip since P6-242b — it used to be here,
            // P6-182), and that does not weaken the sentence above — it is the
            // distinction it draws. While this pill is open there are two
            // readers of `/api/music`; the feed's is the older answer and its own
            // as-of stamp says so.
            <MusicPanel directory={directory} now={now} docPath={docPath} />
          ) : open.kind === 'watch' ? (
            // 🔴 W2b (P6-244). THE THIRD PROJECTION-BACKED PANEL, AND IT
            // FETCHES ITS OWN DATA THE WAY `MusicPanel` DOES, FOR A DIFFERENT
            // REASON. Music's `useMusic` lives inside the panel because MI7
            // wants it POLLED while open, and a page-level hook would poll
            // for a pill nobody has opened. Watch never polls (W2a report §8:
            // the same fingerprint-gated, hourly-floor cadence as movie).
            //
            // ⚠️ THE HEADER FEED READS `/api/watch` TOO. V2-7b added a page-level
            // `useWatch()` here for it; P6-242b moved that call to the signed-in
            // shell (`Layout.jsx`), which is where the feed draws. `useWatch`
            // itself is UNCHANGED (a single fetch on mount, no poll) — it is simply
            // called a second time, by the shell, rather than folded into one
            // shared call: THIS panel's own call still needs to re-fetch on its OWN
            // schedule (mount, `refresh()`, `confirm()` after a write) with no
            // page-level flag to keep in step, the same reason music keeps two
            // callers instead of one.
            //
            // ⚠️ SO WHILE THIS PILL IS OPEN THERE ARE TWO READERS OF
            // `/api/watch` — the shell's and this panel's own (`WatchPanel`'s
            // internal `useWatch()` call). They can disagree: a write this panel
            // confirms updates ITS OWN copy immediately, while the feed's stays
            // whatever it fetched when the shell mounted until the page itself
            // reloads. `useMusicOnce`'s own note states the identical trade for
            // music; this is that trade again, on a THIRD projection.
            //
            // `userId` is passed through because `you` cannot be published on
            // `3a/watch.json` (ADR-0003 D5 — see `watch.js`'s header) and must
            // be derived client-side by matching this member's own id against
            // the payload's `members[]`.
            <WatchPanel
              directory={directory}
              now={now}
              userId={typeof userId === 'string' ? userId : null}
              docPath={docPath}
            />
          ) : open.kind === 'hoops' ? (
            // 🔴 P6-328b (GN-1b). A PROJECTION-BACKED PANEL, AND IT FETCHES AND POLLS ITS OWN
            // DATA INSIDE ITSELF, for the reason `MusicPanel` does: the board is polled every fifteen
            // seconds while a game is on, and this selector renders one panel at a time, so mounting IS
            // "open" and unmounting IS "closed" — a page-level hook would poll for a pill nobody opened.
            // Nothing else on the page reads `/api/hoops/*`, so there is no second reader to disagree
            // with it (the header's feed does not carry hoops).
            <HoopsPanel docPath={docPath} />
          ) : (
            <StubPanel
              branch={open}
              row={featureRow(commands.features, open.feature)}
              docPath={docPath}
            />
          )}
        </div>
      </div>

      {/* Where the names came from and when they were last checked. §2 requires
          it wherever staleness could mislead, and a page that names five people
          is exactly that. `loading` prints nothing — nothing has failed yet. */}
      {note ? (
        <p className="d2d-portal__directorynote d2d-micro" data-testid="portal-directory-note">
          {note}
        </p>
      ) : null}
    </article>
  )
}
